import {
  type EditorId,
  type ProjectScript,
  type ResolvedKeybindingsConfig,
  type ThreadId,
} from "@t3tools/contracts";
import { memo } from "react";
import GitActionsControl from "../GitActionsControl";
import { DiffIcon } from "lucide-react";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import ProjectScriptsControl, { type NewProjectScriptInput } from "../ProjectScriptsControl";
import { Toggle } from "../ui/toggle";
import { SidebarCollapsedTrigger } from "../ui/sidebar";
import { OpenInPicker } from "./OpenInPicker";
import {
  formatOperatorsSessionMetadata,
  IS_OPERATORS_APP,
  type OperatorsSession,
} from "~/operators";
import { cn } from "~/lib/utils";

interface ChatHeaderProps {
  activeThreadId: ThreadId;
  activeThreadTitle: string;
  activeOperatorsSession?: OperatorsSession | undefined;
  activeProjectName: string | undefined;
  isGitRepo: boolean;
  openInCwd: string | null;
  activeProjectScripts: ProjectScript[] | undefined;
  preferredScriptId: string | null;
  keybindings: ResolvedKeybindingsConfig;
  availableEditors: ReadonlyArray<EditorId>;
  diffToggleShortcutLabel: string | null;
  gitCwd: string | null;
  diffOpen: boolean;
  showDiffToggle?: boolean;
  onRunProjectScript: (script: ProjectScript) => void;
  onAddProjectScript: (input: NewProjectScriptInput) => Promise<void>;
  onUpdateProjectScript: (scriptId: string, input: NewProjectScriptInput) => Promise<void>;
  onDeleteProjectScript: (scriptId: string) => Promise<void>;
  onToggleDiff: () => void;
}

export const ChatHeader = memo(function ChatHeader({
  activeThreadId,
  activeThreadTitle,
  activeOperatorsSession,
  activeProjectName,
  isGitRepo,
  openInCwd,
  activeProjectScripts,
  preferredScriptId,
  keybindings,
  availableEditors,
  diffToggleShortcutLabel,
  gitCwd,
  diffOpen,
  showDiffToggle,
  onRunProjectScript,
  onAddProjectScript,
  onUpdateProjectScript,
  onDeleteProjectScript,
  onToggleDiff,
}: ChatHeaderProps) {
  const operatorsMode = IS_OPERATORS_APP;
  // Operators diffs come from stored turn checkpoints, not a local git repo.
  // Business mode hides the toggle; callers pass false for that personal view.
  const diffAvailable = operatorsMode || isGitRepo;
  const showDiffControl = showDiffToggle ?? true;
  const operatorsSessionMetadata = activeOperatorsSession
    ? formatOperatorsSessionMetadata(activeOperatorsSession)
    : null;

  return (
    <div
      className={cn("flex min-w-0 flex-1 gap-2", operatorsMode ? "items-start" : "items-center")}
    >
      <div
        className={cn(
          "flex min-w-0 flex-1 overflow-hidden",
          operatorsMode ? "items-start gap-2" : "items-center gap-2 sm:gap-3",
        )}
      >
        <SidebarCollapsedTrigger
          className={cn("size-7 shrink-0", operatorsMode && "self-center")}
        />
        {operatorsMode ? (
          <div className="min-w-0 flex-1">
            <h2
              className="min-w-0 truncate text-sm font-medium text-foreground"
              title={activeThreadTitle}
            >
              {activeThreadTitle}
            </h2>
            {operatorsSessionMetadata ? (
              <p className="truncate text-xs text-muted-foreground/60">
                {operatorsSessionMetadata}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <h2
              className="min-w-0 shrink truncate text-sm font-medium text-foreground"
              title={activeThreadTitle}
            >
              {activeThreadTitle}
            </h2>
            {activeProjectName && (
              <Badge variant="outline" className="min-w-0 shrink truncate">
                {activeProjectName}
              </Badge>
            )}
            {activeProjectName && !isGitRepo && (
              <Badge variant="outline" className="shrink-0 text-[10px] text-amber-700">
                No Git
              </Badge>
            )}
          </>
        )}
      </div>
      <div className="@container/header-actions flex min-w-0 flex-1 items-center justify-end gap-2 self-center @sm/header-actions:gap-3">
        {!operatorsMode && activeProjectScripts && (
          <ProjectScriptsControl
            scripts={activeProjectScripts}
            keybindings={keybindings}
            preferredScriptId={preferredScriptId}
            onRunScript={onRunProjectScript}
            onAddScript={onAddProjectScript}
            onUpdateScript={onUpdateProjectScript}
            onDeleteScript={onDeleteProjectScript}
          />
        )}
        {!operatorsMode && activeProjectName && (
          <OpenInPicker
            keybindings={keybindings}
            availableEditors={availableEditors}
            openInCwd={openInCwd}
          />
        )}
        {!operatorsMode && activeProjectName ? (
          <GitActionsControl gitCwd={gitCwd} activeThreadId={activeThreadId} />
        ) : null}
        {showDiffControl ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Toggle
                  className="shrink-0"
                  pressed={diffOpen}
                  onPressedChange={onToggleDiff}
                  aria-label="Toggle diff panel"
                  variant="outline"
                  size="xs"
                  disabled={!diffAvailable}
                >
                  <DiffIcon className="size-3" />
                </Toggle>
              }
            />
            <TooltipPopup side="bottom">
              {!diffAvailable
                ? "Diff panel is unavailable because this project is not a git repository."
                : diffToggleShortcutLabel
                  ? `Toggle diff panel (${diffToggleShortcutLabel})`
                  : "Toggle diff panel"}
            </TooltipPopup>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
});
